import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBffClient } from './client';
import type { AiService, CreateAiServiceRequest, AddAiServiceDomainRequest, AddFileUploadDomainRequest } from '../types/ai-service';

export function useAiServices() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['ai-services'],
    queryFn: () => client.get<AiService[]>('/bff/ai-services'),
  });
}

export function useAiService(serviceId: string) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['ai-services', serviceId],
    queryFn: () => client.get<AiService>(`/bff/ai-services/${serviceId}`),
    enabled: !!serviceId,
  });
}

export function useCreateAiService() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateAiServiceRequest) => client.post('/bff/ai-services', request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}

export function useAddAiServiceDomain(serviceId: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddAiServiceDomainRequest) =>
      client.post(`/bff/ai-services/${serviceId}/domains`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services', serviceId] });
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}

export function useToggleAiService() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: string) =>
      client.post(`/bff/ai-services/${serviceId}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}

export function useDeleteAiService() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: string) =>
      client.delete(`/bff/ai-services/${serviceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}

export function useAddFileUploadDomain(serviceId: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddFileUploadDomainRequest) =>
      client.post(`/bff/ai-services/${serviceId}/file-upload-domains`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services', serviceId] });
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}

export function useRemoveFileUploadDomain(serviceId: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) =>
      client.delete(`/bff/ai-services/${serviceId}/file-upload-domains/${domainId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-services', serviceId] });
      queryClient.invalidateQueries({ queryKey: ['ai-services'] });
    },
  });
}
